/**
 * Chronological patch notes for the Institutional Sandbox.
 * Add the newest entry at the TOP of `PATCH_NOTES`.
 */
import { useState } from "react";

const PATCH_NOTES = [
  {
    version: "v2.9.5",
    date: "2026-06-01",
    title: "Limited Edition, Referrals, AI Efficiency & Identity Lockdown",
    highlights: [
      "📧 Referral system — invite a friend by Gmail address from Account → Friends → Refer a Friend. Real HTML email delivery via Resend. Hit 10 unique invites to unlock the Limited Edition 'Yea I Brought my Friends' badge.",
      "🎖️ 5 Limited-Edition Badges (first 1000 only): 'Yea I Been here Awhile' (10-day login streak), 'Yea I'm an Elite' (Elite tier), 'Yea I Brought my Friends' (10 referrals), 'Yea I'ma Guild Leader' (create a guild), 'Yea I Got this from the shop' (own all store SKUs).",
      "👑 5 Supreme 1-of-1 Animated Badges: 'Yea I'ma sweat' (first to 32/32 achievements), 'Yea I'm the best' (first #1 across all leaderboards), 'Yea i'ma Billionaire' (first $1B PnL), 'Yea i'm famous' (first to 500 followers), 'I was here before you' (Owner exclusive — already granted on first owner login).",
      "🤖 AI efficiency — Claude trade reviews now skip on losing trades under $5 (saves ~15% tokens), cache identical trade signatures so repeat reviews are free, and a new 'Quick Grade' mode routes to Claude Haiku 4.5 (~5× cheaper than Sonnet).",
      "🛡️ Username constraints — alphanumeric only, 3-15 characters, no spaces, emojis, dashes, or restricted words. Existing usernames grandfathered.",
      "📨 Gmail-only signup — @gmail.com is now enforced at registration. Stops disposable-email signups cold.",
      "🚫 Guild names + usernames now run through the profanity blocklist on creation/rename.",
      "💬 Public profile bio is now visible to all visitors (was previously self-only, defeating the purpose).",
      "🎯 Strikes admin form — type the user's USERNAME or email instead of raw user_id. Fixes the 'user not found' error from the search bar.",
      "🎵 Music persistence — fixed the cutoff bug where leaving the Music tab and coming back killed playback. Audio src is now managed imperatively so route changes don't reload the element.",
      "❤️ New 'Support Us' tab under Account — Instagram, Facebook, and X links to the official Institutional Sandbox accounts.",
      "📅 Daily Challenge — global random NY day per UTC date confirmed; auto-finishes at NY close (16:00 EST).",
    ],
  },
  {
    version: "v2.8.8",
    date: "2026-05-22",
    title: "Gifts, Strikes, Daily-Day Lock & Account Recovery",
    highlights: [
      "🎁 New 'Gift' button next to every Buy button — send any badge/SKU to a friend. From any public profile, the new 'Send Gift' icon top-right pre-fills the recipient. Gifts are FINAL (no refunds).",
      "📩 Recipient gets an auto-DM from the server: '🎁 [Sender] sent you the [Badge Name]!'",
      "🏷️ Three new emotion badges ($2.99 each): 'Finally Break Even', 'This Trade Got Me Stressed', 'Stand Proud You Are Profitable'.",
      "📅 Daily Challenge upgraded — everyone globally now gets the SAME random NY day per UTC date (apples-to-apples leaderboard). When 16:00 EST hits on that day, your challenge auto-closes any open position and locks the chart.",
      "🚫 Profanity censor — slurs and obscenities (incl. Spanish + Chinese) now render as *** in Global, Guild, and DM chats. Existing hard-blocklist for slurs/self-harm still rejects outright.",
      "🖼️ Click-to-reveal NSFW blur on every uploaded chat image — images load blurred by default; tap to view.",
      "🛡️ Admin Moderation Log now has a 'Strikes' tab. Strike a user with a reason — 3 active strikes within 72h = auto-ban. Strikes auto-expire in 72h. Only admin/owner can see and issue strikes.",
      "🆘 Landing-page 'Support' button — locked-out users can submit a recovery ticket directly from the home page. Routes to institutionalsandboxsupport@gmail.com.",
      "💣 'Delete Account' under Security tab — permanent self-delete with checkbox confirmation. Cascades across trades, journal, billing, social graph, guild memberships.",
      "🧹 UI copy cleaned — removed 'test mode' / 'no live processor' / 'framework_only' debug labels from Store, Subscription, and Billing screens.",
    ],
  },
  {
    version: "v2.7.0",
    date: "2026-05-20",
    title: "Self-Service Refunds + Real Stripe on Store",
    highlights: [
      "🛒 Store SKUs now go through real Stripe Checkout — clicking Buy redirects to Stripe, the payment lands in your dashboard, item drops into your inventory automatically.",
      "💸 New 'Refund' button in Account → Billing — one-click self-refunds inside the first 24 hours of any purchase. Item is revoked and you can't repurchase it for 7 days (anti-abuse).",
      "👑 Owner-only freebies — only the seed Owner account skips Stripe for QA. Other admins now go through real checkout like everyone else.",
      "🧾 Recent Purchases panel on the Billing tab shows the last 30 days with refund-window countdown.",
    ],
  },
  {
    version: "v2.6.8",
    date: "2026-05-12",
    title: "Color Names, Store Pagination & Achievements Live",
    highlights: [
      "🎨 Color Display Name now actually colors your name — everywhere (profile header, leaderboards, chat, guild rosters).",
      "🛒 Store paginated — 8 items per page.",
      "🔒 Paid items now locked for non-admins until the Stripe live key is whitelisted.",
      "🔊 Custom Execution Sound — Settings tab now has an upload widget (visible only if you own the SKU).",
      "🏆 32 Achievements are now event-driven or lazily evaluated.",
      "🌍 Flags now show next to every sender in Global chat and Guild chat.",
      "🎯 New sign-in branding — the hooded-trader logo now welcomes you on the login & register screens.",
    ],
  },
  {
    version: "v2.5.2",
    date: "2026-04-20",
    title: "Flags, Guild Roles & Trailing Stops Polish",
    highlights: [
      "🌍 Sign-up redesign — Username + Gmail + National Flag dropdown.",
      "🛡️ Guild gamification overhaul — leaders can appoint up to 2 Officers; Officers can kick non-officer members.",
      "📬 Guild join flow — joining now sends an approval request.",
      "🏁 Daily Challenge 'Rank' button opens the brand-new Challenge Points leaderboard.",
      "🐛 Leaderboard cross-tab crash fixed.",
      "🎚️ 'Live' renamed to 'Delay' on the chart toggle.",
      "⚡ Performance audit — leaderboard fires ~2 GETs/user (down from 100).",
      "🎯 AI trade review token cap — Claude reviews run inside a 450-token budget.",
    ],
  },
  {
    version: "v2.3.9",
    date: "2026-04-14",
    title: "Achievements, Trailing Stops & Background Music",
    highlights: [
      "🏆 New Achievements tab with an 8×4 glassmorphism trophy grid — 32 unlockables.",
      "🎖️ EXP Rank leaderboard replaces the old All-Time tab.",
      "🐛 Leaderboard restored — fixed motor cursor bug.",
      "🗣️ Chats are now character-based: 35 in Global, 50 in Guild.",
      "📈 Stop-Loss can now be dragged above entry on LONGs (trailing stops).",
      "🎵 Music keeps playing in the background when you leave the Music tab.",
    ],
  },
  {
    version: "v2.1.6",
    date: "2026-03-30",
    title: "Rock & Roll Refactor",
    highlights: [
      "🎸 Free Library now ships a Rock track plus Country, Jazz, K-Pop, and Soul/R&B.",
      "🏷️ Four new shop badges with art wired in.",
      "♻️ Dashboard refactored — modals, order panel, marker-jump popup and challenge HUD split into their own files.",
    ],
  },
  {
    version: "v2.0.0",
    date: "2026-03-23",
    title: "INSTITUTIONAL SANDBOX — Phase 1-4 Drop",
    highlights: [
      "🛡️ Mandatory ToS acknowledgement gate on first login.",
      "🎯 Daily Challenge — one attempt per NY session.",
      "🏷️ Profile: bio field (30-word cap), 5 free renames then $0.99 Rename Item.",
      "🥇 Dynamic Top-#1 badges: Daily/Weekly/Monthly/Yearly auto-grant & auto-revoke.",
      "🔍 Global Search button + Admin Search button.",
      "🖼️ Image upload button on every chat surface.",
      "📌 Admins can hover any Global Chat message to Pin (1 min) or Delete.",
      "📁 'The Files' — full audit log of every ban / restriction / role change.",
      "🎵 Music tab: 5-track Epidemic Sound free library + Volume slider.",
      "🛍️ Store additions: Custom Execution Sound ($2.99), Rename Item ($0.99).",
    ],
  },
  {
    version: "v1.9.0",
    date: "2026-03-13",
    title: "Guilds, Badges & Voice DMs",
    highlights: [
      "🏰 Guild system: Pro/Elite can create guilds ($5/mo), max 30 members.",
      "🎖️ Badge system with auto-grants — Elite + Admin.",
      "🛍️ Account → Store: Colored display names ($4.99), exclusive badges ($2.99 each), Personal Music Upload unlock ($10).",
      "🎤 Voice DMs — push-to-record messages up to 120s.",
      "🎵 Account → Music tab with preset playlists + personal upload library.",
      "🛡️ Admin Social Management: freeze global chat, mute, delete, broadcast, dissolve guilds.",
    ],
  },
  {
    version: "v1.8.0",
    date: "2026-02-26",
    title: "Institutional Core",
    highlights: [
      "Rebranded ICT Sandbox → Institutional Sandbox.",
      "Data tiering: Free 30d / Pro 120d / Elite 180d historical depth.",
      "Jump-to-Date modal for precision navigation.",
      "Drawing toolkit gets a lock toggle.",
      "DM notification: red dot on profile photo + sound on new message.",
      "Patch Notes browser baked into Account → Patch Notes.",
      "Social graph: follow/unfollow, friends list, block & report.",
      "Owner role hardening — Owner is immune to admin demote/delete.",
      "Live Global Chat sidebar.",
    ],
  },
  {
    version: "v1.7.0",
    date: "2026-02-16",
    title: "Profiles & Challenge Framework",
    highlights: [
      "Public profiles at /u/:userId with online dot + member duration.",
      "Privacy tab: profile visibility, DM permissions.",
      "Daily Challenge framework — $10K isolated, 4-trade cap, 100-pt scoring engine.",
      "Auto-Journal directly from the AI review modal.",
      "Journal entries are now editable.",
    ],
  },
  {
    version: "v1.6.0",
    date: "2026-02-05",
    title: "Leaderboard Cycles",
    highlights: [
      "EST trading-calendar windows (daily/weekly/monthly/yearly).",
      "Live countdown timer on the leaderboard modal.",
      "30s heartbeat → online/offline dot.",
      "SMT pane minimize + symbol swap dropdown.",
      "Account → Settings (ICT overlays, audio alerts, auto-AI-review).",
    ],
  },
  {
    version: "v1.5.0",
    date: "2026-01-28",
    title: "Monetization Layer",
    highlights: [
      "Stripe Checkout for Pro ($19) and Elite ($49).",
      "Self-service subscription cancellation.",
      "Daily / Weekly / Yearly leaderboard tabs.",
      "Owner-Admin tier override + Feature Flag rollout system.",
      "Free-tier ad slot.",
    ],
  },
  {
    version: "v1.4.0",
    date: "2026-01-15",
    title: "Charting Toolkit",
    highlights: [
      "Drawing toolkit (freehand, eraser, 7-color palette).",
      "Definitions library refreshed with real-chart photos.",
      "Trade Journal with AI pattern analytics (Pro+).",
      "Profile photo upload (base64 in Mongo).",
    ],
  },
];

export default function PatchNotesModal({ onClose }) {
  const [active, setActive] = useState(PATCH_NOTES[0].version);
  const curr = PATCH_NOTES.find(p => p.version === active) || PATCH_NOTES[0];
  return (
    <div data-testid="patch-notes-modal" className="fixed inset-0 bg-black/80 backdrop-blur-md z-50 flex items-center justify-center p-4 font-mono">
      <div className="bg-[#0f131b] border border-slate-800 rounded-2xl max-w-3xl w-full h-[80vh] flex flex-col overflow-hidden">
        <header className="px-5 py-3 border-b border-slate-800 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-emerald-600/20 text-emerald-300 rounded-lg border border-emerald-500/30"><i className="fa-solid fa-clipboard-list"/></div>
            <div>
              <div className="text-white font-bold text-sm">Patch Notes</div>
              <div className="text-[10px] text-slate-500">Platform improvements, newest first</div>
            </div>
          </div>
          <button data-testid="patch-close" onClick={onClose} className="text-slate-500 hover:text-rose-400 text-xl"><i className="fa-solid fa-xmark"/></button>
        </header>
        <div className="flex-1 grid grid-cols-[200px_1fr] overflow-hidden">
          <nav className="border-r border-slate-800 overflow-y-auto py-2">
            {PATCH_NOTES.map((p, i) => (
              <button key={p.version} data-testid={`patch-tab-${p.version}`}
                onClick={() => setActive(p.version)}
                className={`w-full text-left px-4 py-2 text-xs border-l-2 transition-all ${
                  active === p.version
                    ? "bg-emerald-500/10 border-emerald-400 text-emerald-200 font-bold"
                    : "border-transparent text-slate-400 hover:bg-slate-800/40"}`}>
                <div className="font-mono font-bold">{p.version}{i === 0 && <span className="ml-1 text-[8px] text-amber-300">CURRENT</span>}</div>
                <div className="text-[10px] text-slate-500">{p.date}</div>
              </button>
            ))}
          </nav>
          <section className="overflow-y-auto p-6 space-y-3">
            <div className="flex items-baseline justify-between">
              <h2 className="text-2xl font-black text-white">{curr.title}</h2>
              <span className="text-[10px] text-slate-500 uppercase tracking-widest">{curr.version} · {curr.date}</span>
            </div>
            <ul className="space-y-2">
              {curr.highlights.map((h, i) => (
                <li key={i} className="text-sm text-slate-300 flex gap-2">
                  <i className="fa-solid fa-check text-emerald-400 mt-1"/> <span>{h}</span>
                </li>
              ))}
            </ul>
          </section>
        </div>
      </div>
    </div>
  );
}
